/**
 * EntityPicker — combobox-подбор reference-сущности (AK-385, экран 3 фильтры).
 *
 * Универсальный picker поверх reference-эндпоинтов (crops / pests / diseases /
 * weeds / pesticide registrants). Popover + поиск-инпут + список результатов.
 * Debounce поиска, состояния loading/empty. Значение = id, наружу отдаём
 * {id, name}. Клавиатурно доступен (кнопки), не hover-only.
 */
import { useEffect, useMemo, useState } from 'react';
import { Check, ChevronsUpDown, Loader2, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { api } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';

export interface EntityOption {
  id: string;
  name: string;
}

type NameTranslations = Record<string, string>;
interface RefRow {
  id: string;
  name?: string;
  name_translations?: NameTranslations;
}
interface RefEnvelope {
  items: RefRow[];
}

/** Descriptor источника: как строить URL + как достать имя из строки. */
export interface EntitySource {
  /** Базовый путь reference-эндпоинта (напр. `/api/v1/reference/crops`). */
  path: string;
  /** true → отдаёт `{items:[...]}` с name_translations; false → массив {id,name}. */
  translated?: boolean;
  /**
   * Дополнительные query-параметры выборки — например `{ crop_id }`, чтобы
   * сорта сузились до выбранной культуры (AK-704). Участвуют в ключе кэша:
   * без этого react-query отдал бы под тем же ключом список ДРУГОЙ культуры.
   */
  params?: Record<string, string>;
}

function rowName(row: RefRow): string {
  return row.name_translations?.ru ?? row.name ?? '—';
}

export function EntityPicker({
  source,
  value,
  onChange,
  placeholder = 'Выберите…',
  disabled = false,
}: {
  source: EntitySource;
  value: EntityOption | null;
  onChange: (v: EntityOption | null) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 250);
    return () => clearTimeout(t);
  }, [term]);

  const searchParam = source.translated ? 'search' : 'q';
  // Ключ кэша строим из УЖЕ собранного хвоста параметров: пара
  // (path, search) без фильтра склеила бы разные культуры в один ключ.
  const extra = useMemo(() => {
    const parts = Object.entries(source.params ?? {})
      .filter(([, v]) => v !== '')
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`);
    return parts.length ? `&${parts.join('&')}` : '';
  }, [source.params]);

  const q = useQuery({
    queryKey: ['entity-picker', source.path, debounced, extra],
    queryFn: async () => {
      const url = `${source.path}?${searchParam}=${encodeURIComponent(debounced)}&limit=30${extra}`;
      const data = await api.get<RefEnvelope | RefRow[]>(url);
      const items = Array.isArray(data) ? data : (data.items ?? []);
      return items.map((r) => ({ id: r.id, name: rowName(r) }) as EntityOption);
    },
    enabled: open,
    staleTime: 30_000,
  });

  const options = useMemo(() => q.data ?? [], [q.data]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          <span className={cn('truncate', !value && 'text-muted-foreground')}>
            {value ? value.name : placeholder}
          </span>
          <span className="flex items-center gap-1">
            {value && (
              <X
                className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(null);
                }}
              />
            )}
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <div className="relative border-b border-border p-2">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            autoFocus
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Начните вводить…"
            className="pl-8"
          />
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          {q.isLoading && (
            <div className="flex items-center gap-2 px-3 py-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Загрузка…
            </div>
          )}
          {!q.isLoading && options.length === 0 && (
            <div className="px-3 py-3 text-sm text-muted-foreground">
              Ничего не найдено.
            </div>
          )}
          {options.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => {
                onChange(opt);
                setOpen(false);
              }}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm hover:bg-accent"
            >
              <Check
                className={cn(
                  'h-4 w-4',
                  value?.id === opt.id ? 'opacity-100 text-primary' : 'opacity-0',
                )}
              />
              <span className="truncate">{opt.name}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
